import isEmpty from 'lodash/isEmpty'
import type { RefundInformationItem, PromotionInformationDetail } from './interface'
import { useDevice } from '#imports'
import { notificationPopup } from '~/utils/modal/notify.ts'
import { toastPopup } from '~/utils/modal/toast'
import { TRANSACTION_API, USER_API } from '~/configs/api'
import { PROMOTION_PLAN_TYPE } from '~/constants'
import { selectTypeBetIndirect } from '~/resources/account/cashback.ts'
import { getPromotionBanners } from '~/resources/promotion'
import { modalList } from '~/resources/popup-notification.ts'
import { usePromotionUserStore } from '~/stores/promotion-user.store.ts'
import { usePackagesStore } from '~/stores/packages.store.ts'
import { useApi } from '~/composables/common/use-api'
import { ResponseStatus } from '~/composables/common/interface'

export function usePromotion() {
  const { isMobile } = useDevice()
  const { request } = useApi()
  const isCanceling = ref(false)
  const userStore = usePromotionUserStore()
  const storePackages = usePackagesStore()
  const { $notify } = useNuxtApp()
  const isCommissionType = ref(false)
  const isUsingPromotion = ref(false)
  const todayBet = ref('0')

  let promotionUser = userStore.promotionUser
  let promotionPlan = promotionUser?.plan
  let refundInformation = reactive<RefundInformationItem[]>([])
  const promotionInformationDetail = reactive<PromotionInformationDetail>({
    title: '',
    subTitle: '',
    imgSrc: '',
    totalRound: 0,
    currentRound: 0,
    ratioRound: 0
  })
  watch(
    () => userStore.promotionUser,
    () => {
      promotionUser = userStore.promotionUser
      promotionPlan = promotionUser?.plan
      isCommissionType.value = promotionPlan?.package_id === PROMOTION_PLAN_TYPE.COMMISSION
      isUsingPromotion.value = !!(promotionPlan?.package_id && !isCommissionType.value)
      refundInformation.splice(0)
      if (isCommissionType.value) {
        refundInformation.push(
          ...[
            {
              title: 'Hoàn trả hôm qua',
              imgSrc: '/assets/images/pages/account/refund-yesterday.svg',
              value: formatAmountUnit(promotionUser.yesterdayRefund ?? 0, 1)
            },
            {
              title: 'Tổng hoàn trả',
              imgSrc: '/assets/images/pages/account/refund-total.svg',
              value: formatAmountUnit(promotionUser.totalRefund ?? 0, 1)
            }
          ]
        )
      }
      if (isUsingPromotion.value) {
        refundInformation = [
          {
            title: 'Số tiền nạp',
            imgSrc: '/assets/images/pages/account/icon-deposit.svg',
            value: formatAmountUnit(promotionUser.deposit_amount ?? 0, 1)
          },
          {
            title: 'Số tiền khuyến mãi',
            imgSrc: '/assets/images/pages/account/icon-receive.svg',
            value: formatAmountUnit(promotionUser.promotion_amount ?? 0, 1)
          }
        ]

        const promotionFiltered = getPromotionBanners().find(item => item.packageId === promotionPlan?.package_id)

        if (promotionFiltered) {
          promotionInformationDetail.title = promotionFiltered.title
          promotionInformationDetail.subTitle = promotionFiltered.subTitle
          promotionInformationDetail.imgSrc = promotionFiltered.accountPageUrl
          promotionInformationDetail.banner = promotionFiltered.packageName
        }
        const turnOver = promotionUser.turnover || 0
        const dividedNumber =
          promotionUser.rolling && promotionUser.multiplier ? promotionUser.rolling / promotionUser.multiplier : 0
        promotionInformationDetail.currentRound = dividedNumber && turnOver ? Math.floor(turnOver / dividedNumber) : 0
        promotionInformationDetail.totalRound = promotionUser?.multiplier ?? 0
        promotionInformationDetail.calculated_turnover = promotionUser.calculated_turnover ?? 0
        promotionInformationDetail.ratioRound =
          (promotionInformationDetail.calculated_turnover / promotionInformationDetail.totalRound) * 100
        promotionInformationDetail.turnOver = formatAmountUnit(turnOver)
        promotionInformationDetail.rolling = formatAmountUnit(promotionUser.rolling ?? 0)
        promotionInformationDetail.percentage = (turnOver / promotionUser.rolling!) * 100
      }
    },
    { immediate: true }
  )
  watch(
    () => isCommissionType.value,
    value => {
      if (value) {
        isCanceling.value = false
      }
    },
    { immediate: true }
  )
  // TODO UPDATE API
  const refundIndirectInformation: RefundInformationItem[] = [
    // {
    //   title: 'Tổng cược hôm nay',
    //   imgSrc: '/assets/images/pages/account/today-bet.svg',
    //   value: '0'
    // },
    // {
    //   title: 'Hoàn trả hôm qua',
    //   imgSrc: '/assets/images/pages/account/refund-yesterday.svg',
    //   value: '0'
    // },
    // {
    //   title: 'Tổng hoàn trả',
    //   imgSrc: '/assets/images/pages/account/refund-total.svg',
    //   value: '6.789'
    // }
  ]

  const filterPackageVisible = computed(() => {
    const listPromoPackage = storePackages.packages
    const packageId = promotionPlan?.package_id
    if (!packageId) {
      return listPromoPackage
    }
    const filterIds = {
      [PROMOTION_PLAN_TYPE.COMMISSION]: [PROMOTION_PLAN_TYPE.COMMISSION, PROMOTION_PLAN_TYPE.FIRST_50],
      [PROMOTION_PLAN_TYPE.WELCOME]: [PROMOTION_PLAN_TYPE.COMMISSION, PROMOTION_PLAN_TYPE.FIRST_50],
      [PROMOTION_PLAN_TYPE.FIRST_50]: [PROMOTION_PLAN_TYPE.COMMISSION, PROMOTION_PLAN_TYPE.WELCOME]
    }

    const filterId = filterIds[packageId] || [PROMOTION_PLAN_TYPE.COMMISSION, PROMOTION_PLAN_TYPE.WELCOME]

    return listPromoPackage.filter(item => filterId.includes(item.id))
  })

  async function setPromotion(isCanceledPromotion?: boolean): Promise<void> {
    if (isEmpty(userStore.promotionUser?.plan) || isCanceledPromotion) {
      try {
        const { data, status } = await request(TRANSACTION_API.ACCOUNT_PLAN, {
          method: 'GET'
        })
        if (status === ResponseStatus.OK && data) {
          userStore.setPromotionUser(data)
        }
      } catch {
        /* empty */
      }
    }
  }

  async function getPromotionPackages(): Promise<void> {
    try {
      const { data, status } = await request(USER_API.PROMOTION_PACKAGE, {
        method: 'GET'
      })
      if (status === ResponseStatus.OK && data) {
        storePackages.setPackages(data)
      }
    } catch {
      /* empty */
    }
  }

  const setTodayBet = async () => {
    try {
      const response = await request(USER_API.TODAY_BET, { method: 'GET' })
      if (response?.status === ResponseStatus.OK && response?.data) {
        todayBet.value = (response.data as { totalStake: string }).totalStake
      }
    } catch {
      /* empty */
    }
  }

  const confirmCancelPromotion = (): void => {
    if (isCanceling.value) {
      return
    }
    notificationPopup(modalList.CONFIRM_CANCEL_PROMOTION)
  }
  const cancelPromotion = async (): Promise<void> => {
    if (isCanceling.value) {
      return
    }
    isCanceling.value = true
    try {
      const planId = promotionPlan?.id
      const response = await request(TRANSACTION_API.CANCEL_PROMOTION, {
        method: 'PUT',
        body: {
          plan_id: planId
        }
      })
      if (response?.status === ResponseStatus.OK) {
        await setPromotion(true)
        await getPromotionPackages()
        toastPopup({ title: 'Huỷ khuyến mãi thành công', type: 'success', isMobile })
      } else {
        notificationPopup(modalList.CANCEL_PROMOTION_FAILED)
      }
    } catch {
      isCanceling.value = false
      notificationPopup(modalList.ERROR_SYSTEM)
    }
  }

  const depositConfirmCancelPromotion = (): void => {
    $notify({
      title: 'Thông báo',
      html:
        'Tạo phiếu nạp tiền sẽ yêu cầu việc hủy chương trình khuyến mãi hiện tại <span class="color-red-light">(tài ' +
        'khoản Quý khách sẽ về 0)</span>. Quý khách có muốn tiếp tục?',
      icon: '/assets/images/popup/warning.webp',
      cancelButtonText: 'Bỏ qua',
      confirmButtonText: 'Đồng ý',
      confirmButtonFunc: (): void => {
        void cancelPromotion()
      }
    })
  }

  const alertWithdrawlPromotion = (message?: string): void => {
    const popupContent = modalList.WITHDRAW_PROMOTION
    popupContent.description = message ?? popupContent.description
    notificationPopup(popupContent)
  }

  return {
    promotionUser,
    isCommissionType,
    refundInformation,
    refundIndirectInformation,
    todayBet,
    isUsingPromotion,
    promotionInformationDetail,
    filterPackageVisible,
    isCanceling,
    selectTypeBetIndirect,
    setPromotion,
    getPromotionPackages,
    setTodayBet,
    confirmCancelPromotion,
    depositConfirmCancelPromotion,
    alertWithdrawlPromotion,
    cancelPromotion
  }
}
